import { useState, type FC } from "react";
import { toast } from "sonner";

import { Spinner } from "@/components/ui/spinner";

import {
  classificationChanges,
  currentPick,
  type ClassificationPick
} from "../../caseFiles/classification";
import { PickSelects } from "../../caseFiles/components/ClassifyFilesDialog";
import { useCaseTaxonomy } from "../../caseFiles/hooks/useCaseTaxonomy";
import { nodeTitle } from "../../caseFiles/nodeNames";
import type { FsNode } from "../../caseFiles/types";
import { useCaseData } from "../data/CaseDataContext";

type Props = {
  individualId: string;
  node: FsNode;
};

/**
 * The file's category and type on its card, saved as soon as one changes,
 * through the same path as the Category and Type dialog (an attorney's pick,
 * which the organizer leaves alone).
 */
export const FileCardClassification: FC<Props> = ({ individualId, node }) => {
  const { fs } = useCaseData();
  const taxonomy = useCaseTaxonomy(individualId);
  const categories = taxonomy.data?.categories ?? [];
  const [saving, setSaving] = useState(false);

  if (taxonomy.isLoading || !categories.length) return null;

  const save = async (pick: ClassificationPick) => {
    const changes = classificationChanges([node], { [node.id]: pick });
    if (!changes.length) return;
    setSaving(true);
    try {
      await fs.setClassification(changes);
      toast.success("Saved the category and type.");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't save the category."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-end gap-2" data-testid="card-classification">
      <PickSelects
        categories={categories}
        pick={currentPick(node)}
        onChange={(pick) => void save(pick)}
        label={nodeTitle(node)}
        labeled
      />
      {saving ? <Spinner className="mb-2 size-3.5" /> : null}
    </div>
  );
};
